import Icon from '@mdi/react';
import { ButtonVariantProps, buttonStyles, pdsTv } from '@purinanbm/pds-ui';
import { Link as GatsbyLink, GatsbyLinkProps } from 'gatsby';
import * as React from 'react';
import { mdiOpenInNew } from 'src/assets/icons/mdiIcons';
import { prepareAttribute } from 'src/utils/helpers';

const baseLinkStyles = pdsTv({
  variants: {
    analytics: {
      true: 'js-track',
    },
  },
});

/**
 * Link styles across the site.
 *
 * Please make sure if these are changed to also check the prose styles below as well to make sure these are aligned.
 */
export const linkStyles = pdsTv({
  base: [
    'pds-font-bold pds-text-surface-text pds-underline pds-underline-offset-2 pds-transition-colors',
    'hover:pds-text-focus [&_>_svg]:pds-ml-[5px]',
    'focus-visible:pds-text-focus focus-visible:pds-underline focus-visible:pds-underline-offset-2 focus-visible:pds-outline focus-visible:pds-outline-focus',
    'active:pds-text-surface-text active:pds-underline active:pds-underline-offset-2 active:pds-outline active:pds-outline-surface-text',
  ],
  variants: {
    theme: {
      dark: [
        'pds-text-white hover:pds-text-focus-inverse',
        'focus-visible:pds-text-focus-inverse focus-visible:pds-outline-focus-inverse',
        'active:pds-text-surface active:pds-outline-surface',
      ],
      light: '',
      neutral: '',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
});

// These are the link styles imported into the prose plugin
export const proseLinkStyles = [
  'prose-a:pds-font-bold prose-a:pds-underline prose-a:pds-underline-offset-2 hover:prose-a:pds-text-focus',
  'focus-visible:prose-a:pds-text-focus focus-visible:prose-a:pds-outline focus-visible:prose-a:pds-outline-focus',
  'focus-visible:prose-a:pds-underline focus-visible:prose-a:pds-underline-offset-2',
  'active:prose-a:pds-text-surface-text active:prose-a:pds-outline-none active:prose-a:pds-outline active:prose-a:pds-outline-surface-text',
  'active:prose-a:pds-underline active:prose-a:pds-underline-offset-2',
];

interface AttributeProps {
  [key: string]: any;
}

interface LinkPropsExtended {
  analytics?: boolean;
  cmsTheme?: 'light' | 'dark' | 'neutral';
  /** Hides the icon for external links that open new tabs */
  hideExternalIcon?: boolean;
}

export type LinkProps = Omit<GatsbyLinkProps<{}>, 'ref'> & LinkPropsExtended;

/**
 * `BaseLink` is intended to encapsulate the desired link functionality without any styling.
 *  Opinionated components can wrap this and apply the appropriate styling.
 */
export const BaseLink = React.forwardRef<React.ElementRef<'a'>, LinkProps>(
  ({ children, className, to, analytics = true, ...props }, ref) => {
    // This example assumes that any internal link (intended for Gatsby)
    // will start with exactly one slash, and that anything else is external.
    const internal = /^\/(?!\/)/.test(to);
    const file = /\.[0-9a-z]+(?=\?|$)/i.test(to);

    const styles = baseLinkStyles({ className, analytics });

    const attributes: AttributeProps = {};
    // Rewrite attributes to valid html.
    Object.entries(props).forEach(attribute => {
      const key: string = prepareAttribute(attribute[0]);
      attributes[key] = attribute[1];
    });

    const withIcon = attributes?.icon;

    // Use Gatsby Link for internal links, and <a> for others
    if (internal) {
      const handlePath = () => {
        if (!props.lang || !to.includes('/index')) return to;

        // If we're on the home page
        return props.lang === 'en' ? '/' : `/${props.lang}`;
      };
      if (file) {
        return (
          <a ref={ref} href={handlePath()} {...attributes} className={styles}>
            {children}
          </a>
        );
      }

      if (props?.target !== '_blank') {
        return (
          // TODO: fix as any. May not be possible as GatsbyLink does not share object with HtmlAnchorElement"
          <GatsbyLink ref={ref as any} to={handlePath()} {...attributes} className={styles}>
            {children}
          </GatsbyLink>
        );
      }
    }

    return (
      <a ref={ref} href={to} {...attributes} className={styles}>
        {children}
        {props?.target === '_blank' && !props.hideExternalIcon && !withIcon && (
          <Icon title="Open in new window" path={mdiOpenInNew} size={0.6} />
        )}
      </a>
    );
  },
);

const cardLinkStyles = pdsTv({
  base: 'pds-block pds-font-bold pds-no-underline hover:pds-no-underline',
  variants: {
    theme: {
      dark: 'pds-text-white',
      light: 'pds-text-surface-text',
      neutral: 'pds-text-surface-text',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
});

/**
 * `Card Link` is intended for Links wrapping cards where the image and text are not seperate.
 **/
export const CardLink = React.forwardRef<
  React.ElementRef<'a'>,
  LinkProps & { theme?: 'light' | 'dark' | 'neutral'; cmsTheme?: 'light' | 'dark' | 'neutral' }
>(({ children, className, to, theme = 'light', cmsTheme, ...props }, ref) => {
  const computedTheme = cmsTheme ?? theme ?? undefined;
  const styles = React.useMemo(() => {
    return cardLinkStyles({ theme: computedTheme, className });
  }, [className, computedTheme]);

  return (
    <BaseLink ref={ref} to={to} className={styles} {...props}>
      {children}
    </BaseLink>
  );
});

/**
 * `Link` is intended for inline links and non-cta type links
 */
export const Link = React.forwardRef<
  React.ElementRef<'a'>,
  LinkProps & { theme?: 'light' | 'dark' | 'neutral'; cmsTheme?: 'light' | 'dark' | 'neutral' }
>(({ children, className, to, theme = 'light', cmsTheme, ...props }, ref) => {
  const computedTheme = cmsTheme ?? theme ?? undefined;
  const styles = React.useMemo(() => {
    return linkStyles({ theme: computedTheme, className });
  }, [className, computedTheme]);

  return (
    <BaseLink ref={ref} to={to} className={styles} {...props}>
      {children}
    </BaseLink>
  );
});

/**
 * `ButtonLink` is intended for CTA type links
 */
export const ButtonLink = React.forwardRef<React.ElementRef<'a'>, LinkProps & ButtonVariantProps>(
  ({ children, className, to, buttonColor, buttonStyle, size, width, ...props }, ref) => {
    const styles = React.useMemo(() => {
      return buttonStyles({
        buttonColor,
        buttonStyle,
        size,
        width,
        className: ['pds-no-underline', className],
      });
    }, [buttonColor, buttonStyle, size, width, className]);

    return (
      <BaseLink ref={ref} to={to} className={styles} {...props}>
        {children}
      </BaseLink>
    );
  },
);
